import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Title,
  Text,
  Button,
  Stack,
  Group,
  Paper,
  Badge,
  useMantineColorScheme,
  Center,
  Loader,
  Modal,
  ActionIcon,
  Tooltip,
  Grid,
} from '@mantine/core';
import { IconPlayerPause, IconPlayerPlay, IconDeviceFloppy, IconX, IconBook, IconTag, IconAlertTriangle } from '@tabler/icons-react';
import examenService from '../services/examenService';
import {
  IniciarExamenResponse,
  RespuestaUsuario,
  ExamenEnProgreso
} from '../types/examen.types';
import QuejaSugerenciaButton from '../components/QuejaSugerenciaButton';
import RecuperarExamenModal from '../components/RecuperarExamenModal';
import { authService } from '../services/authService';
import { examenStorage } from '../utils/examenStorage';

const ExamenSimulacroPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { colorScheme } = useMantineColorScheme();
  const currentUser = authService.getCurrentUserFromStorage();

  const [examenData, setExamenData] = useState<IniciarExamenResponse | null>(null);
  const [preguntaActual, setPreguntaActual] = useState(0);
  const [respuestas, setRespuestas] = useState<Map<number, string | null>>(new Map());
  const [tiempoInicio, setTiempoInicio] = useState<Date | null>(null);
  const [tiempoTranscurrido, setTiempoTranscurrido] = useState(0);
  const [loading, setLoading] = useState(false);
  const [iniciandoExamen, setIniciandoExamen] = useState(true);
  const [mostrarMapa, setMostrarMapa] = useState(false);
  const [pausado, setPausado] = useState(false);
  const [mensajeCarga, setMensajeCarga] = useState(0);
  const [finalizando, setFinalizando] = useState(false); // Estado para detener auto-guardado
  // Selección temporal (no guardada) de la pregunta actual
  const [seleccionTemporal, setSeleccionTemporal] = useState<string | null>(null);

  // Estado para recuperación de examen
  const [examenEnProgreso, setExamenEnProgreso] = useState<ExamenEnProgreso | null>(null);
  const [mostrarModalRecuperar, setMostrarModalRecuperar] = useState(false);
  const [cargandoRecuperacion, setCargandoRecuperacion] = useState(false);
  // Cuando se viene del simulador con "Continuar", recuperar directo sin mostrar el modal
  const [recuperarDirecto, setRecuperarDirecto] = useState(false);

  // Modal para otro examen en progreso (de otro tipo)
  const [mostrarModalOtroExamen, setMostrarModalOtroExamen] = useState(false);
  const [otroExamen, setOtroExamen] = useState<ExamenEnProgreso | null>(null);

  // Modal de confirmación al pausar y guardar
  const [mostrarModalGuardado, setMostrarModalGuardado] = useState(false);
  
  // Modal de confirmación al finalizar con preguntas sin responder
  const [mostrarModalFinalizarConPendientes, setMostrarModalFinalizarConPendientes] = useState(false);
  const [preguntasSinResponder, setPreguntasSinResponder] = useState(0);

  // Rotar mensajes de carga
  useEffect(() => {
    if (!iniciandoExamen) return;
    
    const interval = setInterval(() => {
      setMensajeCarga((prev) => (prev + 1) % 6);
    }, 2000); // Cambiar mensaje cada 2 segundos
    
    return () => clearInterval(interval);
  }, [iniciandoExamen]);

  // Verificar acceso premium al cargar la página
  useEffect(() => {
    const verificarAcceso = async () => {
      try {
        const { userSubscriptionService } = await import('../services/userSubscriptionService');
        const statusData = await userSubscriptionService.getSubscriptionStatus();
        
        // Usuario es gratuito si no tiene plan o si el plan tiene precio 0
        const esPlanGratuito = statusData?.isFreeplan === true || !statusData?.subscriptionId;
        
        if (esPlanGratuito) {
          // Redirigir a la página de mejorar plan si es usuario gratuito
          navigate('/mejorarplan', { replace: true });
          return;
        }
        
        // Si tiene acceso premium, verificar examen en progreso
        verificarExamenEnProgreso();
      } catch (error) {
        console.error('Error verificando acceso:', error);
        // En caso de error, redirigir al simulador principal por seguridad
        navigate('/estudiante/simulador', { replace: true });
      }
    };

    verificarAcceso();
  }, []);

  const verificarExamenEnProgreso = async () => {
    if (!currentUser) {
      alert('No se pudo obtener el usuario actual');
      navigate('/');
      return;
    }

    try {
      // Verificar solo exámenes ENARM en progreso (permitiendo otros tipos simultáneamente)
      const examen = await examenService.verificarExamenEnProgresoPorTipo(currentUser.id, 'ENARM');
      if (examen) {
        setExamenEnProgreso(examen);
        if (location.state?.continuarDirecto) {
          // Viene del simulador con "Continuar": recuperar directo (el efecto lo dispara),
          // manteniendo la pantalla de carga hasta que esté listo.
          setRecuperarDirecto(true);
        } else {
          setMostrarModalRecuperar(true);
          setIniciandoExamen(false);
        }
      } else {
        // No hay examen ENARM en progreso, iniciar nuevo
        await iniciarExamen();
      }
    } catch (error) {
      console.error('Error verificando examen en progreso:', error);
      await iniciarExamen();
    }
  };

  // Continuar directo desde el simulador: al tener el examen en progreso, recuperarlo sin modal
  useEffect(() => {
    if (recuperarDirecto && examenEnProgreso) {
      setRecuperarDirecto(false);
      (async () => {
        await recuperarExamen();
        setIniciandoExamen(false);
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recuperarDirecto, examenEnProgreso]);

  // Sincronizar selección temporal cuando cambia la pregunta
  useEffect(() => {
    if (!examenData) return;
    const p = examenData.preguntas[preguntaActual];
    if (!p) return;
    // Sincronizar solo cuando cambia la pregunta, no cuando cambian las respuestas
    setSeleccionTemporal(prev => {
      const guardada = respuestas.get(p.orden);
      return guardada ?? null;
    });
  }, [examenData, preguntaActual]);

  useEffect(() => {
    if (!tiempoInicio || pausado) return;

    const interval = setInterval(() => {
      const ahora = new Date();
      const diferencia = Math.floor((ahora.getTime() - tiempoInicio.getTime()) / 1000);
      setTiempoTranscurrido(diferencia);

      // Alerta si quedan 30 minutos
      if (examenData && examenData.tiempoLimiteMin) {
        const tiempoLimite = examenData.tiempoLimiteMin * 60;
        const tiempoRestante = tiempoLimite - diferencia;
        if (tiempoRestante === 1800) {
          alert('⚠️ Quedan 30 minutos para finalizar el examen');
        }
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [tiempoInicio, examenData, pausado]);

  // Auto-guardar progreso cada 30 segundos (detener si está finalizando)
  useEffect(() => {
    if (!examenData || !tiempoInicio || finalizando) return;

    const interval = setInterval(() => {
      guardarProgresoActual();
    }, 30000); // 30 segundos

    return () => clearInterval(interval);
  }, [examenData, preguntaActual, tiempoTranscurrido, pausado, respuestas, finalizando]);

  const togglePausa = () => {
    if (pausado) {
      const tiempoPausado = tiempoTranscurrido;
      setTiempoInicio(new Date(Date.now() - tiempoPausado * 1000));
    } else {
      setTiempoTranscurrido(tiempoTranscurrido);
      guardarProgresoActual(); // Guardar inmediatamente al pausar
    }
    setPausado(!pausado);
  };

  const iniciarExamen = async () => {
    if (!currentUser) {
      alert('No se pudo obtener el usuario actual');
      navigate('/');
      return;
    }

    setIniciandoExamen(true);
    try {
      console.log('Iniciando Simulacro ENARM...');
      const data = await examenService.crearSimulacroENARM(currentUser.id);
      setExamenData(data);
      setTiempoInicio(new Date());
      console.log('✅ Simulacro ENARM iniciado:', data);
    } catch (error) {
      console.error('❌ Error al iniciar simulacro:', error);
      alert('Error al iniciar el simulacro ENARM.');
      navigate('/');
    } finally {
      setIniciandoExamen(false);
    }
  };

  const recuperarExamen = async () => {
    if (!examenEnProgreso) return;

    setCargandoRecuperacion(true);
    try {
      const progreso = await examenService.recuperarProgreso(examenEnProgreso.intentoId);

      // Restaurar estado completo
      setExamenData(progreso.examenData);
      setPreguntaActual(progreso.preguntaActual);
      setTiempoTranscurrido(progreso.tiempoTranscurrido);
      setPausado(progreso.pausado || false);

      // Restaurar respuestas
      const respuestasMap = new Map<number, string | null>();
      Object.entries(progreso.respuestas).forEach(([orden, respuesta]) => {
        respuestasMap.set(parseInt(orden), respuesta);
      });
      setRespuestas(respuestasMap);

      // Calcular tiempo de inicio ajustado
      const tiempoAjustado = new Date(Date.now() - progreso.tiempoTranscurrido * 1000);
      setTiempoInicio(tiempoAjustado);

      setMostrarModalRecuperar(false);

      console.log('✅ Examen recuperado exitosamente');
    } catch (error) {
      console.error('Error recuperando examen:', error);
      alert('Error al recuperar el examen');
    } finally {
      setCargandoRecuperacion(false);
    }
  };

  const comenzarNuevoExamen = async () => {
    if (!examenEnProgreso) return;

    setCargandoRecuperacion(true);
    try {
      // Abandonar el examen anterior
      await examenService.abandonarExamen(examenEnProgreso.intentoId);
      examenStorage.limpiarProgreso();

      setMostrarModalRecuperar(false);
      // Iniciar nuevo examen
      await iniciarExamen();
    } catch (error) {
      console.error('Error abandonando examen:', error);
      alert('Error al abandonar el examen anterior');
    } finally {
      setCargandoRecuperacion(false);
    }
  };

  const guardarProgresoActual = async () => {
    if (!examenData) return;

    try {
      // Convertir Map a objeto
      const respuestasObj: Record<number, string> = {};
      respuestas.forEach((valor, clave) => {
        if (valor) respuestasObj[clave] = valor;
      });

      const request = {
        intentoId: examenData.intentoId,
        preguntaActual,
        tiempoTranscurrido,
        pausado,
        respuestas: respuestasObj
      };

      // Guardar en backend
      await examenService.guardarProgreso(request);

      // Guardar en localStorage como backup
      examenStorage.guardarProgreso(request);

      console.log('💾 Progreso guardado');
    } catch (error) {
      console.error('Error guardando progreso:', error);
    }
  };

  const pausarYGuardar = async () => {
    if (!examenData) return;

    setLoading(true);
    try {
      // Convertir Map a objeto
      const respuestasObj: Record<number, string> = {};
      respuestas.forEach((valor, clave) => {
        if (valor) respuestasObj[clave] = valor;
      });

      const request = {
        intentoId: examenData.intentoId,
        preguntaActual,
        tiempoTranscurrido,
        pausado: true, // Marcar como pausado
        respuestas: respuestasObj
      };

      // Guardar en backend
      await examenService.guardarProgreso(request);

      // Guardar en localStorage como backup
      examenStorage.guardarProgreso(request);

      // Actualizar estado local
      setPausado(true);

      console.log('💾 Progreso guardado y examen pausado');

      // Mostrar modal personalizado
      setMostrarModalGuardado(true);
    } catch (error) {
      console.error('Error al pausar y guardar:', error);
      alert('Error al guardar el progreso del examen.');
    } finally {
      setLoading(false);
    }
  };

  const seleccionarRespuesta = (respuesta: string) => {
    // Solo actualizar la selección temporal; NO guardar aún
    setSeleccionTemporal(respuesta);
  };

  const siguientePregunta = () => {
    if (!examenData) return;
    if (preguntaActual < examenData.preguntas.length - 1) {
      setPreguntaActual(preguntaActual + 1);
    }
  };

  const preguntaAnterior = () => {
    if (preguntaActual > 0) {
      setPreguntaActual(preguntaActual - 1);
    }
  };

  const irAPregunta = (indice: number) => {
    setPreguntaActual(indice);
    setMostrarMapa(false);
  };

  const finalizarExamen = async () => {
    if (!examenData || !tiempoInicio) return;

    const respondidas = respuestas.size;
    const sinResponder = examenData.totalPreguntas - respondidas;

    // Si hay preguntas sin responder, mostrar modal de confirmación
    if (sinResponder > 0) {
      setPreguntasSinResponder(sinResponder);
      setMostrarModalFinalizarConPendientes(true);
      return;
    }

    // Si todas están respondidas, finalizar directamente
    await confirmarFinalizarExamen();
  };

  const confirmarFinalizarExamen = async () => {
    if (!examenData || !tiempoInicio) return;

    setFinalizando(true); // Detener auto-guardado
    setMostrarModalFinalizarConPendientes(false);
    setLoading(true);
    try {
      const respuestasArray: RespuestaUsuario[] = examenData.preguntas.map((pregunta) => ({
        orden: pregunta.orden,
        respuesta: respuestas.get(pregunta.orden) || null,
        tiempoSeg: Math.floor(tiempoTranscurrido / examenData.totalPreguntas)
      }));

      const resultado = await examenService.finalizarExamen({
        intentoId: examenData.intentoId,
        duracionSeg: tiempoTranscurrido,
        respuestas: respuestasArray
      });

      // Limpiar progreso guardado
      examenStorage.limpiarProgreso();

      navigate(`/estudiante/resultados/${examenData.intentoId}`, { state: { resultado } });
    } catch (error) {
      console.error('Error al finalizar:', error);
      alert('Error al finalizar el examen.');
    } finally {
      setLoading(false);
    }
  };

  const formatearTiempo = (segundos: number): string => {
    const horas = Math.floor(segundos / 3600);
    const mins = Math.floor((segundos % 3600) / 60);
    const secs = segundos % 60;
    return `${horas.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  if (iniciandoExamen) {
    const mensajes = [
      { titulo: 'Generando Simulacro ENARM', subtitulo: 'Preparando tu examen personalizado...' },
      { titulo: 'Seleccionando preguntas', subtitulo: '~280 reactivos de alto nivel...' },
      { titulo: 'Calibrando dificultad', subtitulo: 'Ajustando complejidad según ENARM...' },
      { titulo: 'Organizando casos clínicos', subtitulo: 'Casos reales para tu práctica...' },
      { titulo: 'Preparando tus especialidades', subtitulo: 'Cubriendo todas las áreas médicas...' },
      { titulo: 'Últimos ajustes', subtitulo: 'Casi listo para comenzar...' }
    ];

    const mensajeActual = mensajes[mensajeCarga];

    return (
      <Box
        style={{
          minHeight: '100vh',
          background:
            colorScheme === 'dark'
              ? 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)'
              : 'linear-gradient(135deg, #f7f3ee 0%, #f2ede6 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Stack align="center" gap="xl" style={{ maxWidth: '500px', padding: '24px', width: '100%' }}>
          {/* Loader */}
          <Loader size={80} color={colorScheme === 'dark' ? 'gray' : 'dark'} />

          {/* Título con animación */}
          <Stack align="center" gap="sm" style={{ textAlign: 'center', width: '100%' }}>
            <Title
              order={3}
              style={{
                animation: 'fadeIn 0.5s ease-in-out',
                color: colorScheme === 'dark' ? '#e2e8f0' : '#1e293b',
              }}
            >
              {mensajeActual.titulo}
            </Title>
            <Text
              size="sm"
              c="dimmed"
              style={{
                animation: 'fadeIn 0.5s ease-in-out 0.2s both',
              }}
            >
              {mensajeActual.subtitulo}
            </Text>
          </Stack>

          {/* Barra de progreso con ancho fijo */}
          <Box style={{ width: '100%', maxWidth: '400px' }}>
            <Box
              style={{
                height: '3px',
                background: colorScheme === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)',
                borderRadius: '2px',
                overflow: 'hidden',
                position: 'relative',
              }}
            >
              <Box
                style={{
                  height: '100%',
                  width: `${((mensajeCarga + 1) / 6) * 100}%`,
                  background: colorScheme === 'dark' ? '#e2e8f0' : '#1e293b',
                  transition: 'width 0.5s ease',
                  position: 'absolute',
                  left: 0,
                  top: 0,
                }}
              />
            </Box>
            <Text size="xs" c="dimmed" mt="xs" ta="center">
              {Math.round(((mensajeCarga + 1) / 6) * 100)}%
            </Text>
          </Box>

          <style>{`
            @keyframes fadeIn {
              from {
                opacity: 0;
                transform: translateY(10px);
              }
              to {
                opacity: 1;
                transform: translateY(0);
              }
            }
          `}</style>
        </Stack>
      </Box>
    );
  }

  // Si hay modales activos, mostrarlos aunque no haya examenData
  if (!examenData && !mostrarModalRecuperar && !mostrarModalOtroExamen) {
    return <Center style={{ minHeight: '100vh' }}><Text>Error al cargar el simulacro</Text></Center>;
  }

  const pregunta = examenData?.preguntas[preguntaActual];
  const respuestaSeleccionada = seleccionTemporal;
  const respondidas = respuestas.size;

  const OptionCard: React.FC<{
    value: string;
    label: React.ReactNode;
    selected: boolean;
    disabled?: boolean;
    onSelect: (value: string) => void;
  }> = ({ value, label, selected, disabled, onSelect }) => (
    <Paper
      withBorder
      radius="md"
      p="md"
      role="button"
      tabIndex={disabled ? -1 : 0}
      onClick={() => !disabled && onSelect(value)}
      onKeyDown={(e) => {
        if (!disabled && (e.key === 'Enter' || e.key === ' ')) {
          e.preventDefault();
          onSelect(value);
        }
      }}
      style={{
        cursor: disabled ? 'not-allowed' : 'pointer',
        borderColor: selected ? (colorScheme === 'dark' ? '#64748b' : '#94a3b8') : undefined,
        borderWidth: selected ? 2 : 1,
        boxShadow: 'none',
        backgroundColor: selected
          ? (colorScheme === 'dark' ? 'rgba(31,41,55,0.7)' : '#f3f4f6')
          : colorScheme === 'dark' ? 'rgba(30, 41, 59, 0.6)' : '#ffffff',
        transition: 'background-color 0.15s ease, border-color 0.15s ease, border-width 0.15s ease',
        userSelect: 'none',
      }}
      onMouseEnter={(e) => {
        if (!disabled && !selected) {
          e.currentTarget.style.backgroundColor = colorScheme === 'dark' ? 'rgba(51, 65, 85, 0.5)' : '#f9fafb';
          e.currentTarget.style.borderColor = colorScheme === 'dark' ? '#475569' : '#d1d5db';
        }
      }}
      onMouseLeave={(e) => {
        // Always reset hover styles, regardless of selected state
        e.currentTarget.style.backgroundColor = selected
          ? (colorScheme === 'dark' ? 'rgba(31,41,55,0.7)' : '#f3f4f6')
          : (colorScheme === 'dark' ? 'rgba(30, 41, 59, 0.6)' : '#ffffff');
        e.currentTarget.style.borderColor = selected ? (colorScheme === 'dark' ? '#64748b' : '#94a3b8') : '';
      }}
    >
      <Group align="flex-start" gap="md" wrap="nowrap">
        <Badge
          size="md"
          variant={selected ? 'filled' : 'light'}
          color={selected ? 'dark' : 'gray'}
          style={{
            width: 32,
            height: 32,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontWeight: 600,
            fontSize: '14px',
          }}
        >
          {value.toUpperCase()}
        </Badge>
        <Text style={{ lineHeight: 1.5, flex: 1, textAlign: 'left' }}>{label}</Text>
      </Group>
    </Paper>
  );

  return (
    <>
      <RecuperarExamenModal
        examen={examenEnProgreso}
        opened={mostrarModalRecuperar}
        onContinuar={recuperarExamen}
        onNuevo={comenzarNuevoExamen}
        loading={cargandoRecuperacion}
      />

      {/* Modal: otro examen en progreso */}
      <Modal
        opened={mostrarModalOtroExamen}
        onClose={() => {
          // No permitir cerrar sin elegir una opción
        }}
        centered
        size="sm"
        radius="lg"
        withCloseButton={false}
        overlayProps={{ blur: 3 }}
        styles={{ content: { borderRadius: '16px' } }}
      >
        <Stack gap="lg">
          <Box>
            <Title order={4} mb="xs">Tienes un examen en progreso</Title>
            <Text size="sm" c="dimmed">
              {otroExamen?.nombreExamen}. Debes elegir continuar ese examen o abandonarlo para iniciar este.
            </Text>
          </Box>
          <Group justify="flex-end" gap="sm">
            <Button 
              variant="default" 
              radius="md" 
              loading={cargandoRecuperacion}
              onClick={async () => {
                if (!otroExamen) return;
                setCargandoRecuperacion(true);
                try {
                  // Abandonar el otro examen primero
                  await examenService.abandonarExamen(otroExamen.intentoId);
                  examenStorage.limpiarProgreso();
                  setMostrarModalOtroExamen(false);
                  setOtroExamen(null);
                  // Iniciar nuevo examen
                  await iniciarExamen();
                } catch (error) {
                  console.error('Error abandonando examen:', error);
                  alert('Error al abandonar el examen anterior');
                  setCargandoRecuperacion(false);
                }
              }}
            >
              Abandonar y empezar este
            </Button>
            <Button variant="filled" color="dark" radius="md" onClick={() => {
              if (!otroExamen) return;
              if (otroExamen.tipoExamen === 'RAPIDO') navigate('/estudiante/simulador/rapido');
              else if (otroExamen.tipoExamen === 'FILTRADO') navigate(`/estudiante/examen-filtrado/${otroExamen.intentoId}`);
            }}>
              Ir a ese examen
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Modal de confirmación al pausar/guardar */}
      <Modal
        opened={mostrarModalGuardado}
        onClose={() => setMostrarModalGuardado(false)}
        centered
        size="sm"
        radius="lg"
        withCloseButton={false}
        overlayProps={{ blur: 3 }}
        styles={{
          content: { borderRadius: '16px' },
        }}
      >
        <Stack gap="lg">
          <Box>
            <Title order={4} mb="xs">Examen pausado</Title>
            <Text size="sm" c="dimmed">
              Examen pausado y guardado. Puedes continuar desde donde lo dejaste.
            </Text>
          </Box>
          <Group justify="flex-end" gap="sm">
            <Button variant="default" radius="md" onClick={() => setMostrarModalGuardado(false)}>
              Cancelar
            </Button>
            <Button variant="filled" color="dark" radius="md" onClick={() => navigate('/estudiante/simulador')}>
              Ir al inicio
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Modal de confirmación al finalizar con preguntas sin responder */}
      <Modal
        opened={mostrarModalFinalizarConPendientes}
        onClose={() => setMostrarModalFinalizarConPendientes(false)}
        centered
        size="sm"
        radius="lg"
        withCloseButton={false}
        overlayProps={{ blur: 3 }}
        styles={{
          content: { borderRadius: '16px' },
        }}
      >
        <Stack gap="lg">
          <Box>
            <Title order={4} mb="xs">
              <Group gap={8} align="center">
                <IconAlertTriangle size={20} color="var(--mantine-color-yellow-7)" />
                Preguntas sin responder
              </Group>
            </Title>
            <Text size="sm" c="dimmed">
              Tienes <strong>{preguntasSinResponder}</strong> {preguntasSinResponder === 1 ? 'pregunta' : 'preguntas'} sin responder. ¿Estás seguro de que quieres finalizar el examen?
            </Text>
            <Text size="xs" c="dimmed" mt="xs">
              Las preguntas sin responder se marcarán como incorrectas.
            </Text>
          </Box>
          <Group justify="flex-end" gap="sm">
            <Button
              variant="default"
              onClick={() => setMostrarModalFinalizarConPendientes(false)}
              radius="md"
            >
              Cancelar
            </Button>
            <Button
              variant="filled"
              color="red"
              onClick={confirmarFinalizarExamen}
              loading={loading}
              radius="md"
            >
              Sí, finalizar
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Solo renderizar el contenido del examen si existe examenData */}
      {examenData && pregunta && (
      <Box
        style={{
          minHeight: '100vh',
          background:
            colorScheme === 'dark'
              ? 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)'
              : 'linear-gradient(135deg, #f7f3ee 0%, #f2ede6 100%)',
          padding: '24px',
        }}
      >
        <Paper
          shadow="md"
          radius="md"
          p="xl"
          withBorder
          style={{
            backgroundColor:
              colorScheme === 'dark' ? '#1e293b' : 'rgba(255,255,255,0.95)',
            maxWidth: '1000px',
            margin: '0 auto'
          }}
        >
          {/* Header */}
          <Group justify="space-between" align="center" mb="xl">
            <Box style={{ textAlign: 'left' }}>
              <Title order={2} mb={4}>Simulacro ENARM</Title>
              <Text size="sm" c="dimmed">{examenData.totalPreguntas} preguntas</Text>
            </Box>
            
            {/* Mensaje de pausa (centrado) */}
            {pausado && (
              <Text size="10px" c="red" fw={500} style={{ flex: 1, textAlign: 'center' }}>
                Pausado - Reanuda para continuar
              </Text>
            )}
            
            {/* Timer, pausa y guardar */}
            <Group gap="sm">
              <Badge 
                color={colorScheme === 'dark' ? 'gray' : 'dark'} 
                variant="filled" 
                size="xl"
                style={{
                  fontSize: '16px',
                  fontWeight: 600,
                  padding: '10px 16px',
                }}
              >
                {formatearTiempo(tiempoTranscurrido)}
              </Badge>
              <Tooltip label={pausado ? "Reanudar" : "Pausar"}>
                <ActionIcon
                  size="xl"
                  variant="filled"
                  color={colorScheme === 'dark' ? 'gray' : 'dark'}
                  onClick={togglePausa}
                >
                  {pausado ? <IconPlayerPlay size={20} /> : <IconPlayerPause size={20} />}
                </ActionIcon>
              </Tooltip>
              
              {/* Guardar y salir (solo icono) */}
              <Tooltip label="Guardar y salir">
                <ActionIcon
                  size="xl"
                  variant="filled"
                  color={colorScheme === 'dark' ? 'gray' : 'dark'}
                  onClick={pausarYGuardar}
                  disabled={loading}
                >
                  <IconDeviceFloppy size={20} />
                </ActionIcon>
              </Tooltip>
            </Group>
          </Group>

          {/* Caso clínico */}
          {pregunta.tipo === 'CASO' && pregunta.casoEstudioContenido && (
            <Paper
              p="md"
              mb="lg"
              withBorder
              style={{
                backgroundColor: colorScheme === 'dark' ? 'rgba(30, 41, 59, 0.6)' : '#fafafa',
                borderLeft: `3px solid ${colorScheme === 'dark' ? 'rgba(148,163,184,0.4)' : '#e5e7eb'}`,
              }}
            >
              <Text fw={500} size="sm" mb="xs" c="dimmed">
                CASO CLÍNICO
              </Text>
              <Text style={{ whiteSpace: 'pre-wrap' }}>
                {pregunta.casoEstudioContenido}
              </Text>
            </Paper>
          )}

          {/* Pregunta */}
          <Box mb="sm">
            <Group justify="space-between" align="flex-start" mb="xs">
              <Title order={4} style={{ flex: 1, textAlign: 'left' }}>
                {preguntaActual + 1}. {pregunta.pregunta}
              </Title>

              {currentUser && (
                <QuejaSugerenciaButton
                  reactivoId={pregunta.tipo === 'REACTIVO' ? pregunta.reactivoId : undefined}
                  preguntaCasoId={pregunta.tipo === 'CASO' ? pregunta.preguntaCasoId : undefined}
                  tipoObjetivo={pregunta.tipo === 'CASO' ? 'PREGUNTA_CASO' : 'REACTIVO'}
                />
              )}
            </Group>

            {/* Especialidad y Tema Badges */}
            <Group gap="xs" style={{ marginTop: '8px' }}>
              {pregunta.especialidadNombre && (
                <Badge
                  size="md"
                  variant="light"
                  color="blue"
                  leftSection={<IconBook size={13} />}
                >
                  {pregunta.especialidadNombre}
                </Badge>
              )}
              {pregunta.temaNombre && (
                <Badge
                  size="md"
                  variant="light"
                  color="cyan"
                  leftSection={<IconTag size={13} />}
                >
                  {pregunta.temaNombre}
                </Badge>
              )}
            </Group>
          </Box>

          {/* Opciones - estilo tarjetas */}
          <Stack gap="sm" mb="xl" style={{ opacity: pausado ? 0.5 : 1, pointerEvents: pausado ? 'none' : 'auto' }}>
            <OptionCard
              value="a"
              label={pregunta.respuestaA}
              selected={respuestaSeleccionada === 'a'}
              disabled={pausado}
              onSelect={seleccionarRespuesta}
            />
            <OptionCard
              value="b"
              label={pregunta.respuestaB}
              selected={respuestaSeleccionada === 'b'}
              disabled={pausado}
              onSelect={seleccionarRespuesta}
            />
            <OptionCard
              value="c"
              label={pregunta.respuestaC}
              selected={respuestaSeleccionada === 'c'}
              disabled={pausado}
              onSelect={seleccionarRespuesta}
            />
            <OptionCard
              value="d"
              label={pregunta.respuestaD}
              selected={respuestaSeleccionada === 'd'}
              disabled={pausado}
              onSelect={seleccionarRespuesta}
            />
          </Stack>

          {/* Footer con navegación y progreso */}
          <Box mt="xl" pt="xl" style={{ borderTop: `1px solid ${colorScheme === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}` }}>
            {/* Barra de información y controles */}
            <Group justify="space-between" align="center" wrap="nowrap" mb="lg">
              {/* Navegación */}
              <Group gap="xs" style={{ flex: 1, justifyContent: 'flex-start' }}>
                <ActionIcon
                  size="lg"
                  variant="default"
                  onClick={preguntaAnterior}
                  disabled={preguntaActual === 0}
                  style={{
                    opacity: preguntaActual === 0 ? 0.3 : 1,
                  }}
                >
                  ←
                </ActionIcon>
                
                {/* Mostrar flecha derecha solo si no están todas respondidas */}
                {respuestas.size < examenData.totalPreguntas && (
                  <ActionIcon
                    size="lg"
                    variant="default"
                    onClick={siguientePregunta}
                    disabled={preguntaActual >= examenData.totalPreguntas - 1}
                    style={{
                      opacity: preguntaActual >= examenData.totalPreguntas - 1 ? 0.3 : 1,
                    }}
                  >
                    →
                  </ActionIcon>
                )}
                
                {/* Tachita para finalizar - solo si NO están todas respondidas */}
                {respuestas.size < examenData.totalPreguntas && (
                  <Tooltip label="Finalizar examen">
                    <ActionIcon
                      size="lg"
                      variant="filled"
                      color="#c0392b"
                      style={{ backgroundColor: '#c0392b' }}
                      onClick={finalizarExamen}
                    >
                      <IconX size={18} />
                    </ActionIcon>
                  </Tooltip>
                )}
              </Group>

              {/* Info central - siempre centrado */}
              <Group gap="xs" style={{ display: 'flex', justifyContent: 'center' }}>
                <Badge color="gray" variant="light" size="lg">
                  {preguntaActual + 1} / {examenData.totalPreguntas}
                </Badge>
                <Button size="xs" variant="default" onClick={() => setMostrarMapa(true)}>
                  Mapa
                </Button>
              </Group>

              {/* Botones de acción a la derecha.
                  En la última pregunta solo se muestra "Finalizar" (que además
                  guarda la respuesta seleccionada antes de finalizar). */}
              <Group gap="sm" style={{ flex: 1, justifyContent: 'flex-end' }}>
                {preguntaActual === examenData.totalPreguntas - 1 ? (
                  <Button
                    variant="filled"
                    color="dark"
                    size="sm"
                    onClick={() => {
                      if (!examenData || !pregunta) return;
                      // Guardar la selección de la última pregunta antes de finalizar.
                      const nuevas = new Map(respuestas);
                      if (seleccionTemporal) nuevas.set(pregunta.orden, seleccionTemporal);
                      else nuevas.delete(pregunta.orden);
                      setRespuestas(nuevas);
                      guardarProgresoActual();
                      finalizarExamen();
                    }}
                    disabled={loading}
                    radius="md"
                  >
                    {loading ? 'Finalizando...' : 'Finalizar'}
                  </Button>
                ) : (
                  <Button
                    variant="light"
                    color="dark"
                    size="sm"
                    onClick={() => {
                      if (!examenData || !pregunta) return;
                      const nuevas = new Map(respuestas);
                      if (seleccionTemporal) nuevas.set(pregunta.orden, seleccionTemporal);
                      else nuevas.delete(pregunta.orden);
                      setRespuestas(nuevas);
                      guardarProgresoActual();
                      setPreguntaActual(preguntaActual + 1);
                    }}
                    disabled={loading}
                    radius="md"
                  >
                    Guardar respuesta
                  </Button>
                )}
              </Group>
            </Group>
          </Box>
        </Paper>

        {/* Modal del mapa de navegación */}
        <Modal
          opened={mostrarMapa}
          onClose={() => setMostrarMapa(false)}
          title={<Title order={3}>Mapa de Navegación</Title>}
          size="xl"
          centered
          radius="lg"
          overlayProps={{ blur: 3 }}
          styles={{
            content: {
              borderRadius: '16px',
              backgroundColor: colorScheme === 'dark' ? '#1e293b' : '#ffffff',
            },
            header: {
              backgroundColor: colorScheme === 'dark' ? '#1e293b' : '#ffffff',
            },
            body: {
              backgroundColor: colorScheme === 'dark' ? '#1e293b' : '#ffffff',
            },
          }}
        >
          <Stack gap="md">
            {/* Estadísticas rápidas */}
            <Group gap="md" mb="md">
              <Paper
                p="xs"
                withBorder
                style={{
                  flex: 1,
                  backgroundColor: colorScheme === 'dark' ? 'rgba(34, 197, 94, 0.1)' : 'rgba(34, 197, 94, 0.05)',
                  borderColor: 'rgba(34, 197, 94, 0.3)',
                }}
              >
                <Text size="xs" c="dimmed" ta="center">Respondidas</Text>
                <Text size="xl" fw={700} ta="center" style={{ color: colorScheme === 'dark' ? '#4ade80' : '#16a34a' }}>
                  {respondidas}
                </Text>
              </Paper>
              <Paper
                p="xs"
                withBorder
                style={{
                  flex: 1,
                  backgroundColor: colorScheme === 'dark' ? 'rgba(148, 163, 184, 0.1)' : 'rgba(148, 163, 184, 0.05)',
                }}
              >
                <Text size="xs" c="dimmed" ta="center">Sin responder</Text>
                <Text size="xl" fw={700} ta="center" c="dimmed">
                  {examenData.totalPreguntas - respondidas}
                </Text>
              </Paper>
            </Group>

            {/* Grid de preguntas */}
            <Grid gutter="xs">
              {examenData.preguntas.map((p, index) => {
                const isActual = index === preguntaActual;
                const isRespondida = respuestas.has(p.orden);
                
                return (
                  <Grid.Col key={p.orden} span={1.5}>
                    <Button
                      variant={isActual ? 'filled' : isRespondida ? 'light' : 'outline'}
                      color={isActual ? (colorScheme === 'dark' ? 'gray' : 'dark') : isRespondida ? 'green' : 'gray'}
                      onClick={() => irAPregunta(index)}
                      fullWidth
                      size="md"
                      radius="md"
                      style={{
                        height: '48px',
                        fontSize: '14px',
                        fontWeight: isActual ? 700 : 500,
                      }}
                    >
                      {index + 1}
                    </Button>
                  </Grid.Col>
                );
              })}
            </Grid>

            {/* Leyenda */}
            <Group gap="md" justify="center" mt="md" pt="md" style={{ borderTop: `1px solid ${colorScheme === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}` }}>
              <Group gap="xs">
                <Box
                  style={{
                    width: 20,
                    height: 20,
                    borderRadius: '4px',
                    backgroundColor: colorScheme === 'dark' ? '#64748b' : '#1e293b',
                  }}
                />
                <Text size="xs" c="dimmed">Actual</Text>
              </Group>
              <Group gap="xs">
                <Box
                  style={{
                    width: 20,
                    height: 20,
                    borderRadius: '4px',
                    backgroundColor: 'rgba(34, 197, 94, 0.2)',
                    border: '1px solid rgba(34, 197, 94, 0.5)',
                  }}
                />
                <Text size="xs" c="dimmed">Respondida</Text>
              </Group>
              <Group gap="xs">
                <Box
                  style={{
                    width: 20,
                    height: 20,
                    borderRadius: '4px',
                    backgroundColor: 'transparent',
                    border: `1px solid ${colorScheme === 'dark' ? '#475569' : '#d1d5db'}`,
                  }}
                />
                <Text size="xs" c="dimmed">Sin responder</Text>
              </Group>
            </Group>
          </Stack>
        </Modal>
      </Box>
      )}
    </>
  );
};

export default ExamenSimulacroPage;
